import {Component, OnDestroy} from '@angular/core';
import {
  ControlValueAccessor,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  TouchedChangeEvent,
  ValidationErrors,
  Validator,
  Validators
} from '@angular/forms';
import {DigitalSpreader, DigitalSpreaderModel} from '../../../../../../shared/models/vehicle.model';
import {MatOption} from '@angular/material/autocomplete';
import {MatFormField, MatSelect, MatSuffix} from '@angular/material/select';
import {MatError, MatLabel} from '@angular/material/form-field';
import {MatSlideToggle} from '@angular/material/slide-toggle';
import {MatInput} from '@angular/material/input';
import {ConfigurationService} from '../../../../../../configuration/configuration.service';
import {ConfigurationModel} from 'src/app/shared/models/configuration.model';
import {filter} from 'rxjs/operators';
import {Subscription} from 'rxjs';
import {InstantErrorStateMatcherInstance} from '../../../../../../shared/tools/InstantErrorStateMatcher';
import { RateUnitPipe } from '../../../../../../shared/formatting/rate-unit.pipe';

@Component({
  selector: 'app-digital-spreader-configuration',
  imports: [ReactiveFormsModule, MatOption, MatSelect, MatLabel, MatFormField, MatSlideToggle, MatInput, MatError, MatSuffix, RateUnitPipe],
  templateUrl: './digital-spreader-configuration.component.html',
  styleUrls: ['../../../../settings-fields.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: DigitalSpreaderConfigurationComponent,
    },
    {
      provide: NG_VALIDATORS,
      multi: true,
      useExisting: DigitalSpreaderConfigurationComponent,
    },
  ],
})
export class DigitalSpreaderConfigurationComponent implements ControlValueAccessor, Validator, OnDestroy {
  spreaderConfiguration: DigitalSpreader;

  configuration: ConfigurationModel;

  formGroup: FormGroup;
  subscriptions: Subscription[] = [];

  protected readonly spreaderModels = Object.values(DigitalSpreaderModel);
  protected readonly digitalSpreaderModelLabel = {
    [DigitalSpreaderModel.CPS_CIRRUS]: 'CPS Cirrus',
    [DigitalSpreaderModel.CPS_FREEDOM]: 'CPS Freedom',
    [DigitalSpreaderModel.FORCE_AMERICA_GENERIC]: 'Force America (generic)',
  };

  protected readonly InstantErrorStateMatcherInstance = InstantErrorStateMatcherInstance;

  constructor(fb: FormBuilder, configurationService: ConfigurationService) {
    configurationService.sharedConfigurationModel.subscribe(value => {
      this.configuration = value;
    });

    this.formGroup = fb.group({
      model: fb.control(null, { validators: Validators.required }),
      readLiquid: fb.control(false),
      liquidMaxRate: fb.control(null, { validators: Validators.required }),
      readGranular: fb.control(false),
      granularBlastRate: fb.control(null, { validators: Validators.required }),
      granularMaxRate: fb.control(null, { validators: Validators.required }),
    });

    this.subscriptions.push(
      this.formGroup.events.pipe(filter(e => e instanceof TouchedChangeEvent && e.touched)).subscribe(() => this.onTouchedListener())
    );

    this.subscriptions.push(this.formGroup.controls.readLiquid.valueChanges.subscribe(value => this.handleReadLiquidValueChange(value)));

    this.subscriptions.push(
      this.formGroup.controls.readGranular.valueChanges.subscribe(value => this.handleReadGranularValueChange(value))
    );

    this.subscriptions.push(
      this.formGroup.valueChanges.subscribe(value => {
        const { model, readLiquid, liquidMaxRate, readGranular, granularBlastRate, granularMaxRate } = value;

        this.onChangeListener({
          model,
          settings: {
            readLiquid,
            liquidMaxRate,
            readGranular,
            granularBlastRate,
            granularMaxRate,
          },
        } as DigitalSpreader);
      })
    );
  }

  handleReadLiquidValueChange(readLiquid: boolean) {
    const { liquidMaxRate } = this.formGroup.controls;
    if (readLiquid) {
      liquidMaxRate.enable({ emitEvent: false });
    } else {
      liquidMaxRate.disable({ emitEvent: false });
    }
  }

  handleReadGranularValueChange(readGranular: boolean) {
    const { granularMaxRate, granularBlastRate } = this.formGroup.controls;
    if (readGranular) {
      granularMaxRate.enable({ emitEvent: false });
      granularBlastRate.enable({ emitEvent: false });
    } else {
      granularMaxRate.disable({ emitEvent: false });
      granularBlastRate.disable({ emitEvent: false });
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  writeValue(obj: any) {
    this.spreaderConfiguration = obj || { settings: {} };
    const {
      model,
      settings: { readLiquid, liquidMaxRate, readGranular, granularMaxRate, granularBlastRate },
    } = this.spreaderConfiguration;
    this.formGroup.setValue(
      {
        model: model || null,
        readLiquid: readLiquid || false,
        liquidMaxRate: liquidMaxRate || null,
        readGranular: readGranular || false,
        granularMaxRate: granularMaxRate || null,
        granularBlastRate: granularBlastRate || null,
      },
      { emitEvent: false }
    );

    this.handleReadLiquidValueChange(readLiquid || false);
    this.handleReadGranularValueChange(readGranular || false);
  }

  onChangeListener = _ => {};

  onTouchedListener = () => {};

  registerOnChange(fn: any) {
    this.onChangeListener = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedListener = fn;
  }

  setDisabledState(isDisabled: boolean) {
    // ignore
  }

  validate(control: FormGroup): ValidationErrors | null {
    if (control.invalid || this.formGroup.invalid) {
      return { _control: { valid: false } };
    }
    return null;
  }
}
